/* ============================================================================
   LED.nl — lichtplanner
   ----------------------------------------------------------------------------
   Rekent uit hoeveel armaturen er in een ruimte nodig zijn en tekent waar ze
   moeten hangen.

   De lichtstroommethode:

       N = (E × A) / (Φ × UF × MF)

   E   gewenste verlichtingssterkte (lux), uit de gekozen toepassing
   A   vloeroppervlak (m²)
   Φ   lichtstroom per armatuur (lumen), uit de productdata
   UF  benuttingsfactor — hoeveel van het licht op het werkvlak terechtkomt.
       Volgt uit de ruimte-index k = (L × B) / (Hm × (L + B)), waarbij Hm de
       hoogte van de armaturen bóven het werkvlak is. Een hoge, smalle ruimte
       heeft een lage k en dus een lage UF: veel licht gaat naar de wanden.
   MF  onderhoudsfactor — vervuiling en veroudering. 0,80 schoon, 0,70
       normaal, 0,60 stoffig.

   De UF-tabel hieronder geldt voor een armatuur met brede lichtverdeling in
   een ruimte met lichte wanden (reflectie ongeveer 70/50/20). Tussen de
   punten wordt lineair geïnterpoleerd.

   Alle zichtbare tekst komt uit data-attributen, dus alles is vertaalbaar.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pdp-plan]');
  if (!root) return;

  var section = root.closest('.pdp-plan');
  function $(s, r) { return (r || section).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || section).querySelectorAll(s)); }

  /* -------------------------------------------------------------------------
     Benuttingsfactor per ruimte-index.
     ---------------------------------------------------------------------- */
  var UF_TABEL = [
    [0.60, 0.40], [0.80, 0.46], [1.00, 0.51], [1.25, 0.56],
    [1.50, 0.60], [2.00, 0.66], [2.50, 0.70], [3.00, 0.73],
    [4.00, 0.77], [5.00, 0.79]
  ];

  function benuttingsfactor(k) {
    if (k <= UF_TABEL[0][0]) return UF_TABEL[0][1];
    var laatste = UF_TABEL[UF_TABEL.length - 1];
    if (k >= laatste[0]) return laatste[1];
    for (var i = 0; i < UF_TABEL.length - 1; i++) {
      var a = UF_TABEL[i], b = UF_TABEL[i + 1];
      if (k >= a[0] && k <= b[0]) {
        var deel = (k - a[0]) / (b[0] - a[0]);
        return a[1] + deel * (b[1] - a[1]);
      }
    }
    return 0.55;
  }

  /* -------------------------------------------------------------------------
     Geld opmaken — zelfde aanpak als in pdp-main.js: het formaat wordt
     afgeleid van een bedrag dat Liquid al heeft gerenderd.
     ---------------------------------------------------------------------- */
  var voorbeeld = document.querySelector('[data-pdp-price-incl]');
  var geldTekst = voorbeeld ? voorbeeld.textContent.trim() : '€ 0,00';
  var komma = geldTekst.indexOf(',') > geldTekst.indexOf('.');
  var voorvoegsel = geldTekst.replace(/[\d.,\s]+$/, '').trim() || '€';

  function geld(centen) {
    var v = (Math.round(centen) / 100).toFixed(2).split('.');
    var heel = v[0].replace(/\B(?=(\d{3})+(?!\d))/g, komma ? '.' : ',');
    return voorvoegsel + ' ' + heel + (komma ? ',' : '.') + v[1];
  }

  function getal(n, decimalen) {
    return n.toFixed(decimalen || 0).replace('.', komma ? ',' : '.');
  }

  /* -------------------------------------------------------------------------
     Verdeling over de ruimte.
     Vuistregel uit de verlichtingstechniek: de onderlinge afstand tussen
     armaturen is hooguit ongeveer 1,5 × de hoogte boven het werkvlak, en de
     afstand tot de wand is de helft daarvan. We zoeken de rij/kolom-verdeling
     die het dichtst bij de verhouding van de ruimte ligt, zodat de spreiding
     gelijkmatig is in plaats van alles op één rij.
     ---------------------------------------------------------------------- */
  function verdeel(n, L, B) {
    if (n <= 0) return { rijen: 0, kolommen: 0 };
    var beste = null;
    for (var rijen = 1; rijen <= n; rijen++) {
      var kolommen = Math.ceil(n / rijen);
      // Afstand tussen de armaturen in beide richtingen bij deze verdeling.
      var dx = L / kolommen;
      var dy = B / rijen;
      // Hoe vierkanter het vak per armatuur, hoe gelijkmatiger het licht.
      var afwijking = Math.abs(Math.log(dx / dy));
      // Lege plekken op de laatste rij zijn onhandig; licht bestraffen.
      var leeg = rijen * kolommen - n;
      var score = afwijking + leeg * 0.12;
      if (!beste || score < beste.score) {
        beste = { rijen: rijen, kolommen: kolommen, dx: dx, dy: dy, score: score };
      }
    }
    return beste;
  }

  /* -------------------------------------------------------------------------
     De schets — een plattegrond zoals een installateur hem zou tekenen.

     Opzet:
     - De verhouding van de ruimte klopt altijd: de schaal volgt uit de breedte
       en wordt alleen bijgesteld als de ruimte anders te hoog wordt voor het
       kader. Er is geen minimumhoogte meer, want die drukte lange smalle
       ruimtes plat.
     - Een raster van hele meters geeft gevoel voor maat.
     - De lichtvlekken zitten in één groep met een blur eroverheen. Omdat de
       ellipsen binnen die groep dekkend zijn, tellen overlappingen niet op:
       twee armaturen naast elkaar geven één egale lichtbaan in plaats van een
       donkerder gele vlek. De doorzichtigheid staat op de groep als geheel.
     - Kleuren staan niet hier maar in pdp-custom.css, via klassenamen, zodat
       de schets dezelfde tokens gebruikt als de rest van de pagina.
     ---------------------------------------------------------------------- */
  var svgTeller = 0;

  function teken(L, B, plan, armLengteM) {
    if (!plan || plan.rijen === 0) return '';

    // Afronden houdt de opmaakstring kort en voorkomt subpixel-gerommel.
    function n1(v) { return Math.round(v * 10) / 10; }

    var W = 360;            // tekenbreedte; de svg schaalt mee met het kader
    var mL = 30, mR = 14;   // ruimte voor de maatlijnen
    var mT = 26, mB = 14;
    var vrij = W - mL - mR;
    var maxHoogte = 250;

    var schaal = vrij / L;
    if (B * schaal > maxHoogte) schaal = maxHoogte / B;

    var rB = L * schaal;
    var rH = B * schaal;
    var x0 = mL + (vrij - rB) / 2;   // gecentreerd als de hoogte de maat gaf
    var y0 = mT;
    var H = rH + mT + mB;

    var id = 'pdpplan' + (++svgTeller);
    var vervaging = Math.max(3, Math.min(11, schaal * 0.22));

    var svg = '<svg class="pdp-plan__svg" viewBox="0 0 ' + W + ' ' + n1(H) +
              '" width="100%" xmlns="http://www.w3.org/2000/svg">';

    svg += '<defs>' +
           '<filter id="' + id + 'b" x="-35%" y="-80%" width="170%" height="260%">' +
           '<feGaussianBlur stdDeviation="' + n1(vervaging) + '"/></filter>' +
           '<clipPath id="' + id + 'c"><rect x="' + n1(x0) + '" y="' + n1(y0) +
           '" width="' + n1(rB) + '" height="' + n1(rH) + '" rx="2"/></clipPath>' +
           '</defs>';

    // ---- de vloer ----
    svg += '<rect class="floor" x="' + n1(x0) + '" y="' + n1(y0) + '" width="' + n1(rB) +
           '" height="' + n1(rH) + '" rx="2"/>';

    // ---- raster van hele meters (grover zodra het te dicht wordt) ----
    var stappen = [1, 2, 5, 10, 20], stap = 1;
    for (var si = 0; si < stappen.length; si++) {
      stap = stappen[si];
      if (stap * schaal >= 20) break;
    }
    var raster = '';
    for (var gx = stap; gx < L - 0.01; gx += stap) {
      var px = n1(x0 + gx * schaal);
      raster += '<line x1="' + px + '" y1="' + n1(y0) + '" x2="' + px + '" y2="' + n1(y0 + rH) + '"/>';
    }
    for (var gy = stap; gy < B - 0.01; gy += stap) {
      var py = n1(y0 + gy * schaal);
      raster += '<line x1="' + n1(x0) + '" y1="' + py + '" x2="' + n1(x0 + rB) + '" y2="' + py + '"/>';
    }
    if (raster) svg += '<g class="grid">' + raster + '</g>';

    // ---- waar hangen ze? ----
    var posities = [];
    var geplaatst = 0;
    for (var r = 0; r < plan.rijen; r++) {
      // Aantal op deze rij: een onvolledige rij komt onderaan, niet middenin.
      var opRij = Math.min(plan.kolommen, plan.totaal - geplaatst);
      if (opRij <= 0) break;
      var y = y0 + rH * (r + 0.5) / plan.rijen;
      for (var c = 0; c < opRij; c++) {
        posities.push({ x: x0 + rB * (c + 0.5) / opRij, y: y, opRij: opRij });
        geplaatst++;
      }
    }

    // De armaturen hangen in de lengterichting, dus liggend getekend. Nooit
    // breder dan het vak dat ze bedienen, anders raken ze elkaar.
    var cel = rB / plan.kolommen;
    var armB = Math.max(11, Math.min(armLengteM * schaal, cel * 0.8));
    var armH = Math.max(4, Math.min(8.5, armB * 0.11));

    // ---- het licht ----
    var licht = '';
    var ryLicht = Math.max(armH * 2, (rH / plan.rijen) * 0.32);
    for (var i = 0; i < posities.length; i++) {
      var p = posities[i];
      var rxLicht = Math.max(armB * 0.55, (rB / p.opRij) * 0.28);
      licht += '<ellipse cx="' + n1(p.x) + '" cy="' + n1(p.y) + '" rx="' + n1(rxLicht) +
               '" ry="' + n1(ryLicht) + '"/>';
    }
    svg += '<g clip-path="url(#' + id + 'c)"><g class="glow" filter="url(#' + id + 'b)">' +
           licht + '</g></g>';

    // ---- de armaturen ----
    var armaturen = '';
    for (var j = 0; j < posities.length; j++) {
      var q = posities[j];
      armaturen += '<rect class="lum" x="' + n1(q.x - armB / 2) + '" y="' + n1(q.y - armH / 2) +
                   '" width="' + n1(armB) + '" height="' + n1(armH) +
                   '" rx="' + n1(Math.min(2, armH / 2)) + '"/>';
      if (armH >= 5) {
        armaturen += '<rect class="lum-in" x="' + n1(q.x - armB / 2 + 2.5) + '" y="' +
                     n1(q.y - armH * 0.16) + '" width="' + n1(armB - 5) + '" height="' +
                     n1(armH * 0.32) + '" rx="1"/>';
      }
    }
    svg += armaturen;

    // ---- de wand, over het licht heen ----
    svg += '<rect class="wall" x="' + n1(x0) + '" y="' + n1(y0) + '" width="' + n1(rB) +
           '" height="' + n1(rH) + '" rx="2"/>';

    // ---- hangposities afgestreept op de bovenwand ----
    if (plan.kolommen <= 12) {
      var strepen = '';
      for (var k = 0; k < plan.kolommen; k++) {
        var tx = n1(x0 + rB * (k + 0.5) / plan.kolommen);
        strepen += '<line x1="' + tx + '" y1="' + n1(y0) + '" x2="' + tx + '" y2="' + n1(y0 - 4) + '"/>';
      }
      svg += '<g class="tick">' + strepen + '</g>';
    }

    // ---- maatlijnen: streep met eindstreepjes, label in een wit vakje ----
    function maat(tekst, x1, y1, x2, y2, cx, cy, draai) {
      var dwars = draai ? 'x' : 'y';
      var uit = '<g class="dim"><line x1="' + n1(x1) + '" y1="' + n1(y1) + '" x2="' + n1(x2) +
                '" y2="' + n1(y2) + '"/>';
      // eindstreepjes haaks op de maatlijn
      [[x1, y1], [x2, y2]].forEach(function (pt) {
        if (dwars === 'y') {
          uit += '<line x1="' + n1(pt[0]) + '" y1="' + n1(pt[1] - 3.5) + '" x2="' + n1(pt[0]) +
                 '" y2="' + n1(pt[1] + 3.5) + '"/>';
        } else {
          uit += '<line x1="' + n1(pt[0] - 3.5) + '" y1="' + n1(pt[1]) + '" x2="' + n1(pt[0] + 3.5) +
                 '" y2="' + n1(pt[1]) + '"/>';
        }
      });
      uit += '</g>';
      var breed = tekst.length * 6.2 + 8;
      var g = draai ? '<g transform="rotate(-90 ' + n1(cx) + ' ' + n1(cy) + ')">' : '<g>';
      uit += g +
             '<rect class="dimbg" x="' + n1(cx - breed / 2) + '" y="' + n1(cy - 7.5) +
             '" width="' + n1(breed) + '" height="15"/>' +
             '<text class="dimtxt" x="' + n1(cx) + '" y="' + n1(cy + 4) + '" text-anchor="middle">' +
             tekst + '</text></g>';
      return uit;
    }

    var dimY = y0 - 10;
    svg += maat(getal(L, 1) + ' m', x0, dimY, x0 + rB, dimY, x0 + rB / 2, dimY, false);
    var dimX = x0 - 11;
    svg += maat(getal(B, 1) + ' m', dimX, y0, dimX, y0 + rH, dimX, y0 + rH / 2, true);

    svg += '</svg>';
    return svg;
  }

  /* -------------------------------------------------------------------------
     Rekenen en tonen
     ---------------------------------------------------------------------- */
  var lumen = parseFloat(root.dataset.lumen) || 0;
  var armLengteCm = parseFloat(root.dataset.length) || 120;
  var prijs = parseInt(root.dataset.price, 10) || 0;

  var elCount = $('[data-plan-count]');
  var elCountLabel = $('[data-plan-countlabel]');
  var elSummary = $('[data-plan-summary]');
  var elSketch = $('[data-plan-sketch]');
  var elCaption = $('[data-plan-caption]');
  var elArea = $('[data-plan-area]');
  var elLux = $('[data-plan-lux]');
  var elTotal = $('[data-plan-total]');
  var elPrice = $('[data-plan-price]');
  var elMethod = $('[data-plan-method]');
  var elAddLabel = $('[data-plan-addlabel]');

  // Teksten met plaatshouders staan als sjabloon in de opmaak; we lezen ze
  // één keer uit zodat het invullen ze niet overschrijft.
  var tpl = {
    pieces: elCountLabel ? elCountLabel.textContent.trim() : '',
    summary: section.dataset.tplSummary || '',
    caption: section.dataset.tplCaption || '',
    method: section.dataset.tplMethod || '',
    add: elAddLabel ? elAddLabel.textContent.trim() : ''
  };

  function vul(sjabloon, waarden) {
    var uit = sjabloon;
    Object.keys(waarden).forEach(function (k) {
      uit = uit.split('[[' + k + ']]').join(waarden[k]);
    });
    return uit;
  }

  var laatsteAantal = 0;

  function reken() {
    var L = parseFloat(($('[data-plan-l]') || {}).value) || 0;
    var B = parseFloat(($('[data-plan-b]') || {}).value) || 0;
    var H = parseFloat(($('[data-plan-h]') || {}).value) || 0;
    if (L <= 0 || B <= 0 || H <= 0) return;

    var gekozen = $$('input[name^="pdp-use-"]').filter(function (i) { return i.checked; })[0];
    if (!gekozen) return;
    var lux = parseFloat(gekozen.value) || 300;
    var opWerkblad = gekozen.dataset.workPlane === 'true';

    var mfInput = $$('input[name^="pdp-dirt-"]').filter(function (i) { return i.checked; })[0];
    var MF = mfInput ? parseFloat(mfInput.value) : 0.8;

    // Hoogte van de armaturen boven het vlak waar het licht moet vallen.
    var werkvlak = opWerkblad ? 0.85 : 0;
    var Hm = Math.max(0.5, H - werkvlak);

    var A = L * B;
    var k = (L * B) / (Hm * (L + B));
    var UF = benuttingsfactor(k);

    var exact = (lux * A) / (lumen * UF * MF);
    var N = Math.max(1, Math.ceil(exact));

    // Wat levert dat aantal werkelijk op?
    var werkelijkeLux = (N * lumen * UF * MF) / A;

    var plan = verdeel(N, L, B);
    plan.totaal = N;

    // ---- tonen ----
    if (elCount) elCount.textContent = N;
    if (elCountLabel) {
      elCountLabel.textContent = N === 1 ? (section.dataset.tplPieceOne || tpl.pieces) : tpl.pieces;
    }
    if (elSummary) {
      elSummary.textContent = vul(tpl.summary, {
        n: N, lumen: getal(lumen), opp: getal(A, 1), lux: getal(werkelijkeLux)
      });
    }
    if (elSketch) elSketch.innerHTML = teken(L, B, plan, armLengteCm / 100);
    if (elCaption) {
      elCaption.textContent = vul(tpl.caption, {
        rijen: plan.rijen,
        kolommen: plan.kolommen,
        dx: getal(L / plan.kolommen, 1)
      });
    }
    if (elArea) elArea.textContent = getal(A, 1) + ' m²';
    if (elLux) elLux.textContent = getal(werkelijkeLux) + ' lux';
    if (elTotal) elTotal.textContent = getal(N * lumen) + ' lm';
    if (elPrice) elPrice.textContent = prijs ? geld(N * prijs) : '—';
    if (elMethod) {
      elMethod.textContent = vul(tpl.method, {
        k: getal(k, 2), uf: getal(UF, 2), mf: getal(MF, 2)
      });
    }
    if (elAddLabel) elAddLabel.textContent = vul(tpl.add, { n: N });

    laatsteAantal = N;
  }

  // Alles wat de uitkomst kan veranderen opnieuw laten rekenen.
  $$('input').forEach(function (el) {
    el.addEventListener('input', reken);
    el.addEventListener('change', reken);
  });

  /* -------------------------------------------------------------------------
     Het berekende aantal in de winkelwagen leggen. We zetten de hoeveelheid
     in het bestaande productformulier en klikken de echte knop aan, zodat de
     staffelkorting en de AJAX-afhandeling van het theme gewoon meelopen.
     ---------------------------------------------------------------------- */
  var knop = $('[data-plan-add]');
  if (knop) {
    knop.addEventListener('click', function () {
      var qty = document.querySelector('[data-pdp-qty-input]');
      var atc = document.querySelector('[data-pdp-atc]');
      if (!qty || !atc) return;
      qty.value = laatsteAantal;
      qty.dispatchEvent(new Event('change', { bubbles: true }));
      atc.click();
      if (elAddLabel && section.dataset.tplAdded) {
        var oud = elAddLabel.textContent;
        elAddLabel.textContent = section.dataset.tplAdded;
        setTimeout(function () { elAddLabel.textContent = oud; }, 2500);
      }
    });
  }

  /* -------------------------------------------------------------------------
     "Laat dit nakijken" — een mail met de ingevulde maten en de uitkomst.
     ---------------------------------------------------------------------- */
  var mail = $('[data-plan-mail]');
  if (mail) {
    mail.addEventListener('click', function (e) {
      e.preventDefault();
      var L = ($('[data-plan-l]') || {}).value;
      var B = ($('[data-plan-b]') || {}).value;
      var H = ($('[data-plan-h]') || {}).value;
      var gekozen = $$('input[name^="pdp-use-"]').filter(function (i) { return i.checked; })[0];
      var gebruik = gekozen ? gekozen.dataset.useLabel : '';

      var body = [
        'Hallo,', '',
        'Kunnen jullie dit lichtplan even nakijken?', '',
        'Product: ' + mail.dataset.url,
        'Ruimte: ' + L + ' x ' + B + ' m, plafondhoogte ' + H + ' m',
        'Gebruik: ' + gebruik + (gekozen ? ' (' + gekozen.value + ' lux)' : ''),
        'Berekend: ' + laatsteAantal + ' stuks', '',
        'Met vriendelijke groet,'
      ].join('\n');

      window.location.href = 'mailto:' + mail.dataset.email +
        '?subject=' + encodeURIComponent(mail.dataset.subject || '') +
        '&body=' + encodeURIComponent(body);
    });
  }

  reken();
})();
